import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import SiteFooter from './SiteFooter'
import V2UniversalFooter from './V2UniversalFooter'
import NavSearch from './NavSearch'
import NavAboutDropdown from './NavAboutDropdown'
import NavCapabilitiesDropdown from './NavCapabilitiesDropdown'
import NavThemeToggle from './NavThemeToggle'
import '../versions/v2/v2.css'

/**
 * Shared shell for the v2 design system: accent bar, sticky nav, universal CTA footer, legal strip.
 * Features a fully responsive mobile hamburger menu and slide-out navigation drawer.
 */
const V2Layout = ({ children, pageClass = '' }) => {
    const [themeMode, setThemeMode] = useState(() => {
        return localStorage.getItem('pfc-theme-mode') || 'dark'
    })
    const [activeTheme, setActiveTheme] = useState('dark')

    useEffect(() => {
        const darkQuery = window.matchMedia('(prefers-color-scheme: dark)')

        const updateTheme = () => {
            if (themeMode === 'system') {
                setActiveTheme(darkQuery.matches ? 'dark' : 'light')
            } else {
                setActiveTheme(themeMode)
            }
        }

        updateTheme()

        const listener = () => {
            if (themeMode === 'system') {
                updateTheme()
            }
        }

        darkQuery.addEventListener('change', listener)
        return () => darkQuery.removeEventListener('change', listener)
    }, [themeMode])

    const handleThemeChange = (newMode) => {
        setThemeMode(newMode)
        localStorage.setItem('pfc-theme-mode', newMode)
    }
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
    const [aboutOpen, setAboutOpen] = useState(false)
    const [capabilitiesOpen, setCapabilitiesOpen] = useState(false)
    const [searchQuery, setSearchQuery] = useState('')
    const navigate = useNavigate()

    // Auto-close mobile drawer if viewport is resized beyond mobile breakpoint (900px)
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth > 900) {
                setMobileMenuOpen(false)
            }
        }
        window.addEventListener('resize', handleResize)
        return () => window.removeEventListener('resize', handleResize)
    }, [])

    // Prevent body scrolling when mobile menu drawer is open
    useEffect(() => {
        if (mobileMenuOpen) {
            document.body.style.overflow = 'hidden'
        } else {
            document.body.style.overflow = ''
        }
        return () => {
            document.body.style.overflow = ''
        }
    }, [mobileMenuOpen])

    const handleMobileSearch = (e) => {
        if (e.key === 'Enter' && searchQuery.trim()) {
            setMobileMenuOpen(false)
            navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`)
        }
    }

    return (
        <div className={`version-v2 v2-theme-${activeTheme} ${pageClass}`.trim()}>
            <div className="v2-top-bar" />

            <nav className="v2-navbar">
                <div className="v2-nav-inner">
                    <Link to="/" className="v2-brand" onClick={() => setMobileMenuOpen(false)}>
                        {activeTheme === 'dark' ? (
                            <img src="/dark-logo.png" alt="Precision Fluid Controls" className="v2-brand-logo" style={{ height: '64px', objectFit: 'contain', display: 'block' }} />
                        ) : (
                            <img src="/light-logo.png" alt="Precision Fluid Controls" className="v2-brand-logo" style={{ height: '64px', objectFit: 'contain', display: 'block' }} />
                        )}
                    </Link>

                    {/* Desktop Navigation Links */}
                    <div className="v2-nav-links">
                        <NavAboutDropdown />
                        <Link to="/products">Products</Link>
                        <NavCapabilitiesDropdown />
                        <Link to="/news">News</Link>
                        <Link to="/careers">Careers</Link>
                        <NavSearch />
                        <NavThemeToggle
                            mode={themeMode}
                            activeTheme={activeTheme}
                            onChange={handleThemeChange}
                        />
                        <Link to="/contactus" className="v2-nav-contact">
                            Contact Us
                        </Link>
                    </div>

                    {/* Mobile Menu Hamburger Button */}
                    <button
                        className={`v2-mobile-toggle ${mobileMenuOpen ? 'is-active' : ''}`}
                        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                        aria-label="Toggle navigation menu"
                        aria-expanded={mobileMenuOpen}
                    >
                        <span className="v2-mobile-toggle-box">
                            <span className="v2-mobile-toggle-inner"></span>
                        </span>
                    </button>
                </div>
            </nav>

            {/* Mobile Slide-out Drawer */}
            <div className={`v2-mobile-drawer ${mobileMenuOpen ? 'is-open' : ''}`}>
                <div className="v2-mobile-drawer-content">
                    {/* Search Field */}
                    <div className="v2-mobile-search-wrap">
                        <svg className="v2-mobile-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="11" cy="11" r="8" />
                            <line x1="21" y1="21" x2="16.65" y2="16.65" />
                        </svg>
                        <input
                            type="search"
                            className="v2-mobile-search-input"
                            placeholder="Search site..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onKeyDown={handleMobileSearch}
                        />
                    </div>

                    {/* Navigation Menu */}
                    <div className="v2-mobile-nav-links">
                        {/* Collapsible About Section */}
                        <div className={`v2-mobile-collapsible ${aboutOpen ? 'is-expanded' : ''}`}>
                            <button
                                type="button"
                                className="v2-mobile-collapsible-trigger"
                                onClick={() => setAboutOpen(!aboutOpen)}
                            >
                                <span>About</span>
                                <svg className="v2-mobile-chevron" width="10" height="10" viewBox="0 0 10 10">
                                    <path d="M2 3.5L5 6.5L8 3.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </button>
                            <div className="v2-mobile-collapsible-content">
                                <Link to="/about" onClick={() => setMobileMenuOpen(false)}>About Us</Link>
                                <Link to="/facility" onClick={() => setMobileMenuOpen(false)}>Our Facility</Link>
                                <Link to="/quality" onClick={() => setMobileMenuOpen(false)}>Quality</Link>
                            </div>
                        </div>

                        <Link to="/products" onClick={() => setMobileMenuOpen(false)}>Products</Link>

                        {/* Collapsible Capabilities Section */}
                        <div className={`v2-mobile-collapsible ${capabilitiesOpen ? 'is-expanded' : ''}`}>
                            <button
                                type="button"
                                className="v2-mobile-collapsible-trigger"
                                onClick={() => setCapabilitiesOpen(!capabilitiesOpen)}
                            >
                                <span>Capabilities</span>
                                <svg className="v2-mobile-chevron" width="10" height="10" viewBox="0 0 10 10">
                                    <path d="M2 3.5L5 6.5L8 3.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </button>
                            <div className="v2-mobile-collapsible-content">
                                <Link to="/capabilities" onClick={() => setMobileMenuOpen(false)} style={{ fontWeight: 700 }}>Capabilities</Link>
                                <div className="v2-mobile-sub-sub-links">
                                    <Link to="/capabilities/valve-design" onClick={() => setMobileMenuOpen(false)}>Valve Design</Link>
                                    <Link to="/capabilities/machining" onClick={() => setMobileMenuOpen(false)}>Machining</Link>
                                    <Link to="/capabilities/cleaning" onClick={() => setMobileMenuOpen(false)}>Cleaning & Passivation</Link>
                                    <Link to="/capabilities/assembly-test" onClick={() => setMobileMenuOpen(false)}>Assembly & Test</Link>
                                    <Link to="/capabilities/environmental-testing" onClick={() => setMobileMenuOpen(false)}>Environmental Testing</Link>
                                    <Link to="/capabilities/contract-manufacturing" onClick={() => setMobileMenuOpen(false)}>Custom Component Production</Link>
                                </div>
                            </div>
                        </div>

                        <Link to="/news" onClick={() => setMobileMenuOpen(false)}>News</Link>
                        <Link to="/careers" onClick={() => setMobileMenuOpen(false)}>Careers</Link>
                    </div>

                    {/* Footer Controls & CTA */}
                    <div className="v2-mobile-drawer-footer">
                        <div className="v2-mobile-controls-row">
                            <span style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--v2-nav-link)' }}>Theme</span>
                            <NavThemeToggle
                                mode={themeMode}
                                activeTheme={activeTheme}
                                onChange={handleThemeChange}
                            />
                        </div>
                        <Link to="/contactus" className="v2-mobile-contact-btn" onClick={() => setMobileMenuOpen(false)}>
                            Contact Us
                        </Link>
                    </div>
                </div>
            </div>

            <div className="v2-nav-spacer" />

            {children}

            <V2UniversalFooter />

            <SiteFooter />
        </div>
    )
}

export default V2Layout
